/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {VoucherDiscountType, VoucherItem, VoucherType, VoucherTypeFilter} from "types/entities/VoucherItem";
import {Package} from "types/entities/Package";
import {currencyNumberFormat, getArticleById} from "utils/utility";
import {PackageCategory} from "types/enums/PackageCategory";
import Dates from "utils/Dates";
import {ArticleItem, DiscountedArticleItemPrice} from "types/entities/ArticleItem";
import {DateRange} from "types/entities/PackageItem";
import {VoucherItemValidity} from "types/entities/specific/VoucherItemValidity";
import {ShopArticleItem} from "types/entities/ShopArticleItem";
import BookingAction from "../store/booking/BookingAction";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {useTranslation} from "react-i18next";
import {ArticleTicketType} from "types/enums/ArticleTicketType";

export const useVoucherTypeByArticle = (count?: number) => {
    const {t} = useTranslation();

    const getVoucherType = (voucherItem: VoucherItem): VoucherType => {
        console.log(voucherItem);
        if (!voucherItem.article) {
            return {
                label: "General",
                type: VoucherDiscountType.General,
                valueFormatted: "" + voucherItem.amount_left,
                filterBy: [VoucherTypeFilter.All],
                value: voucherItem.amount_left,
            };
        }

        switch (voucherItem.article.ticket_type) {
            case ArticleTicketType.Access:
                return {
                    label: t("Access"),
                    type: VoucherDiscountType.Access,
                    valueFormatted: voucherItem.amount_left + "x",
                    value: voucherItem.amount_left,
                    filterBy: [VoucherTypeFilter.Article, VoucherTypeFilter.Activity],
                };
            case ArticleTicketType.AccessExtra:
                if (voucherItem.article.active_minutes_after_purchase === 0) {
                    return {
                        label: "",
                        type: VoucherDiscountType.AccessExtra,
                        valueFormatted: t("karting_extra", {count: voucherItem.article.active_minutes_after_purchase / 60}) as string,
                        value: voucherItem.amount_left,
                        filterBy: [VoucherTypeFilter.Article, VoucherTypeFilter.Activity],
                    };
                }
                return {
                    label: t("Access"),
                    type: VoucherDiscountType.AccessExtra,
                    valueFormatted:
                        voucherItem.article.active_minutes_after_purchase == 30
                            ? t("extra_zeit")
                            : (t("stunde_extra", {count: voucherItem.article.active_minutes_after_purchase / 60}) as string),
                    value: voucherItem.amount_left,
                    filterBy: [VoucherTypeFilter.Article, VoucherTypeFilter.Activity],
                };
            case ArticleTicketType.Race:
                return {
                    label: t("Activity discount"),
                    type: VoucherDiscountType.Race,
                    valueFormatted: voucherItem.article.race_count_total + "x",
                    value: voucherItem.article.race_count_total,
                    filterBy: [VoucherTypeFilter.Article, VoucherTypeFilter.Activity],
                };

            case ArticleTicketType.Discount:
                return {
                    label: t("Total discount"),
                    type: VoucherDiscountType.Value,
                    valueFormatted: currencyNumberFormat(voucherItem.article.article_value),
                    value: voucherItem.article.article_value,
                    filterBy: voucherItem.article.activity_id ? [VoucherTypeFilter.Activity] : [VoucherTypeFilter.All],
                };

            case ArticleTicketType.DiscountPercent:
                return {
                    label: t("Percent discount"),
                    type: VoucherDiscountType.Percent,
                    valueFormatted: voucherItem.article.article_value / 100 + "%",
                    value: voucherItem.article.article_value / 100,
                    filterBy: voucherItem.article.activity_id ? [VoucherTypeFilter.Activity] : [VoucherTypeFilter.All],
                };

            case ArticleTicketType.Voucher:
                return {
                    label: t("Credit"),
                    type: VoucherDiscountType.Credit,
                    valueFormatted: currencyNumberFormat(voucherItem.amount_left),
                    value: voucherItem.amount_left,
                    filterBy: voucherItem.article.activity_id ? [VoucherTypeFilter.Activity] : [VoucherTypeFilter.All],
                };

            default:
                return {
                    label: t("General"),
                    type: VoucherDiscountType.General,
                    valueFormatted: "" + voucherItem.amount_left,
                    value: voucherItem.amount_left,
                    filterBy: [VoucherTypeFilter.All],
                };
        }
    };
    return {
        getVoucherType,
    };
};

export const useVoucherSubLabelByPersonLimit = () => {
    const {t} = useTranslation();

    const getVoucherSubLabel = (voucherItem: VoucherItem | undefined): string | null => {
        if (!voucherItem) {
            return null;
        }
        if (!voucherItem.max_persons_count && !voucherItem.min_persons_count) {
            return null;
        }

        if (voucherItem.min_persons_count && voucherItem.max_persons_count) {
            return t("min_max_persons", {
                max: voucherItem.max_persons_count,
                min: voucherItem.min_persons_count,
            });
        }

        if (voucherItem.max_persons_count) {
            return t("max_persons", {max: voucherItem.max_persons_count});
        }

        if (voucherItem.min_persons_count) {
            return t("min_persons", {min: voucherItem.min_persons_count});
        }

        return null;
    };

    return {
        getVoucherSubLabel,
    };
};

export const voucherEligibleForPackages = (voucherItem: VoucherItem) => {
    return voucherItem.article.ticket_type === ArticleTicketType.Race;
};

export const voucherEligibleForActivity = (voucherItem: VoucherItem) => {
    return voucherItem.article.activity_id !== null;
};

export const compareNumbersInArray = (a: number[], b: number[]) => a.filter(e => b.indexOf(e) !== -1);

export const isAlreadyApplied = (appliedVoucherItems, voucherCode) => appliedVoucherItems.filter(appliedItem => appliedItem.code === voucherCode);

export const flatPackages = (packages: Package[]) => packages.map(Package => [...Package.packageItems]).flat();

export const getEligibleActivityFromVouchers = (voucherItems: VoucherItem[], packages: Package[]) => {
    if (voucherItems.length === 0 || packages.length === 0) {
        return null;
    }
    const eligibleVouchers = voucherItems.filter(voucherItem => voucherEligibleForActivity(voucherItem));
    if (eligibleVouchers.length === 0) {
        return null;
    }

    const filteredNonEventPackages = packages.filter(Package => Package.category !== PackageCategory.Event);
    const packageItems = flatPackages(filteredNonEventPackages);
    const eligibleVouchersActivityIds: number[] = eligibleVouchers
        .filter(voucherItem => voucherItem.article.activity_id)
        .map(voucherItem => voucherItem.article.activity_id) as number[];
    const packageItemActivityIds = packageItems.map(packageItem => packageItem.activity_id);
    const eligiblePackageItems = compareNumbersInArray(packageItemActivityIds, eligibleVouchersActivityIds);
    if (!eligiblePackageItems) {
        return null;
    }
    const filteredPackageItems = packageItems.filter(packageItem => {
        return eligiblePackageItems.includes(packageItem.activity_id);
    });

    return filteredPackageItems.map(packageItem => packageItem.package_id);
};

export const getEligibleDiscountsFromVouchers = (voucherItems: VoucherItem[], packages: Package[]) => {
    const {getVoucherType} = useVoucherTypeByArticle();
    if (voucherItems.length === 0 || packages.length === 0) {
        return null;
    }
    const eligibleVouchers = voucherItems.filter(voucherItem => {
        return getVoucherType(voucherItem).type === VoucherDiscountType.Percent || getVoucherType(voucherItem).type === VoucherDiscountType.Value;
    });
    if (eligibleVouchers.length === 0) {
        return null;
    }

    const filteredNonEventPackages = packages.filter(Package => Package.category !== PackageCategory.Event);
    const packageItems = flatPackages(filteredNonEventPackages);
    // const eligibleVouchersActivityIds: number[] = eligibleVouchers.filter(voucherItem => voucherItem.article.activity_id).map(voucherItem => voucherItem.article.activity_id) as number[];
    // const packageItemActivityIds = packageItems.map(packageItem => packageItem.activity_id);
    // const eligiblePackageItems = compareNumbersInArray(packageItemActivityIds, eligibleVouchersActivityIds);
    // if (!eligiblePackageItems) {
    //     return null;
    // }

    return packageItems.map(packageItem => packageItem.package_id);
};

export const getEligiblePackageFromVoucherItem = (voucherItem: VoucherItem, Package: Package): boolean => {
    const {getVoucherType} = useVoucherTypeByArticle();
    if (typeof voucherItem === "undefined") {
        return false;
    }
    const filteredNonEventPackages = Package.category !== PackageCategory.Event;
    if (!filteredNonEventPackages) {
        return false; //Only non-Event packages are eligible to any discounts
    }
    const packageItems = Package.packageItems.flat();
    const packageItemArticleIds = packageItems.map(packageItem => packageItem.article_id);
    const packageItemActivities = packageItems.map(packageItem => packageItem.activity_id);
    if (!packageItemArticleIds.includes(voucherItem.article_id)) {
        if (voucherItem.article.activity_id !== null && typeof voucherItem.article.activity_id !== "undefined") {
            if (!packageItemActivities.includes(voucherItem.article.activity_id)) {
                return false; //if the voucher is tied to activity, and the packageItems don't include this activity
            } else {
                if (getVoucherType(voucherItem).filterBy.includes(VoucherTypeFilter.Activity)) {
                    return true; // activity is included
                } else {
                    return false; // activity is included, but the voucher isn' tied to this activity
                }
            }
        } else {
            if (getVoucherType(voucherItem).filterBy.includes(VoucherTypeFilter.All)) {
                return true; // voucher is of generic type, it's allowed everywhere
            }
            return false; //article isn't included
        }
    } else {
        return true; //article is included
    }
};

export const getEligiblePackagesFromVouchers = (voucherItems: VoucherItem[], packages: Package[]) => {
    if (voucherItems.length === 0 || packages.length === 0) {
        return null;
    }
    const eligibleVouchers = voucherItems.filter(voucherItem => voucherEligibleForPackages(voucherItem));
    if (eligibleVouchers.length === 0) {
        return null;
    }

    const filteredNonEventPackages = packages.filter(Package => Package.category !== PackageCategory.Event);
    const packageItems = flatPackages(filteredNonEventPackages);
    const eligibleVouchersArticleIds = eligibleVouchers.map(voucherItem => voucherItem.article_id);
    const packageItemArticleIds = packageItems.map(packageItem => packageItem.article_id);

    const eligiblePackageItems = compareNumbersInArray(packageItemArticleIds, eligibleVouchersArticleIds);
    if (!eligiblePackageItems) {
        return null;
    }

    const filteredPackageItems = packageItems.filter(packageItem => {
        return eligiblePackageItems.includes(packageItem.article_id);
    });

    return filteredPackageItems.map(packageItem => packageItem.package_id);
};

export const getValidityByWeekDay = (voucherItem: VoucherItem, date: string): VoucherItemValidity => {
    const includeWeekDays = voucherItem.extra.includeWeekDays;
    const excludeWeekDays = voucherItem.extra.excludeWeekDays;
    const limited = includeWeekDays.length > 0 || excludeWeekDays.length > 0;
    const weekDay = Dates.weekDay(date);
    const dateRangeValidity = getValidDateRangeByFilterDate(voucherItem, date);
    const includeWeekDayNames = includeWeekDays.map(weekDay => Dates.weekdayNameTranslated(weekDay));
    const excludeWeekDayNames = excludeWeekDays.map(weekDay => Dates.weekdayNameTranslated(weekDay));
    let include = true;
    let exclude = true;
    let notExpired = true;
    if (includeWeekDays.length > 0 && !includeWeekDays.includes(weekDay)) {
        include = false;
    }

    if (excludeWeekDays.length > 0 && excludeWeekDays.includes(weekDay)) {
        exclude = false;
    }

    const expires_at = voucherItem.expires_at !== null ? new Date(voucherItem.expires_at) : null;
    if (expires_at !== null) {
        if (expires_at < new Date(date)) {
            notExpired = false;
        }
    }

    const valid = include && exclude && notExpired;
    return {
        limited: limited,
        validity: valid,
        includeWeekDays: includeWeekDayNames,
        excludeWeekDays: excludeWeekDayNames,
        excludeDateRange: dateRangeValidity.excluded,
        excludeDateRanges: dateRangeValidity.excludeDateRanges,
        includeDateRange: dateRangeValidity.included,
        includeDateRanges: dateRangeValidity.includeDateRanges,
    };
};

export const getDiscountTotalPrice = (articleItems: ArticleItem[]): number => {
    return articleItems.reduce((agg, cur: ArticleItem) => {
        if (cur.appliedVoucher) {
            const discount = cur.appliedVoucher.old_price - cur.appliedVoucher.new_price;
            return agg + discount;
        }
        return agg;
    }, 0);
};
export const getDiscountedArticleItemPrice = (articleItem: ArticleItem | ShopArticleItem): DiscountedArticleItemPrice => {
    const articleItemArticle = getArticleById(articleItem.article_id);
    if ("appliedVoucher" in articleItem && articleItem.appliedVoucher) {
        return {
            present: true,
            applied: articleItem.appliedVoucher.applicable,
            price: articleItem.appliedVoucher.new_price,
        };
    }
    return {
        present: false,
        applied: false,
        price: articleItemArticle.article_value,
    };
};

export const eligibleVoucherFilterBy = (eligibleVoucher: VoucherItem, voucherTypeFilter: VoucherTypeFilter): boolean => {
    const {getVoucherType} = useVoucherTypeByArticle();
    return getVoucherType(eligibleVoucher).filterBy.includes(voucherTypeFilter);
};

export const getValidDateRangeByFilterDate = (eligibleVoucher: VoucherItem, date: string) => {
    const filterDate = new Date(date);
    const excludeDateRanges = eligibleVoucher.extra.excludeDateRange;
    const includeDateRanges = eligibleVoucher.extra.includeDateRange;

    const isExcluded = excludeDateRanges.reduce((prev: boolean, cur: DateRange) => {
        const agg = filterDate <= new Date(cur.from) || filterDate >= new Date(cur.to);
        if (!prev) {
            return agg;
        }
        return prev && agg;
    }, false);

    const isIncluded = includeDateRanges.reduce((prev: boolean, cur: DateRange) => {
        const agg = filterDate >= new Date(cur.from) && filterDate <= new Date(cur.to);
        if (!prev) {
            return agg;
        }
        return prev && agg;
    }, false);

    return {
        includeDateRanges: includeDateRanges,
        included: isIncluded,
        excludeDateRanges: excludeDateRanges,
        excluded: isExcluded,
    };
};

export const getDateRangeValidity = (eligibleVoucher: VoucherItem, date: string): boolean => {
    if (eligibleVoucher.extra.includeDateRange.length === 0 && eligibleVoucher.extra.excludeDateRange.length === 0) {
        return true;
    }
    const dateRangeValidity = getValidDateRangeByFilterDate(eligibleVoucher, date);
    console.log(dateRangeValidity);
    return dateRangeValidity.included && !dateRangeValidity.excluded;
};

export const getVoucherCodes = (voucherItems: VoucherItem[]) => {
    if (voucherItems.length === 0) {
        return [];
    }
    return voucherItems.map(voucherItem => voucherItem.code) || [];
};

export const useVouchersWithValidOnDate = hallOpening => {
    const dispatch = useDispatch();
    const voucherItems = useTypedSelector(state => state.voucherState.voucherItems);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);

    const getVouchersWithDate = (): boolean => {
        const voucherWithDateIndex = voucherItems.findIndex(voucher => voucher.extra.valid_on_date !== null);
        const voucherItem = voucherWithDateIndex != -1 ? voucherItems[voucherWithDateIndex] : null;
        if (voucherItem) {
            dispatchFilterIfVoucherHasDate(voucherItem);
        }
        return voucherItem === null;
    };

    const dispatchFilterIfVoucherHasDate = (voucherItem: VoucherItem) => {
        const date = voucherItem.extra?.valid_on_date;
        if (date && voucherItem.validity) {
            setNewDateOnFilter(date);
        }
    };

    const setNewDateOnFilter = (date_string: string) => {
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                date: date_string,
            })
        );
        dispatch(BookingAction.setDateChosenByCustomer(true));
    };

    /**
     * Checks if vouchers change the date.
     *
     * If there are no voucher items and a hall opening is defined with a date, the function checks if the date
     * specified in the booking filter is before the hall opening date. If so, it updates the booking filter date
     * with the hall opening date.
     *
     * If there are voucher items, the function finds the index of a voucher item that has a valid date.
     * If found, it dispatches a filter action to apply the voucher item's date to the filter.
     */
    const checkIfVouchersChangeTheDate = () => {
        if (voucherItems.length === 0) {
            if (hallOpening.hallOpeningDefined && hallOpening.hallOpeningDate) {
                const date = Dates.ymd(hallOpening.hallOpeningDate);
                if (new Date(bookingFilter.date).getTime() < hallOpening.hallOpeningDate.getTime()) {
                    setNewDateOnFilter(date);
                }
            }
        }

        if (voucherItems.length !== 0) {
            const voucherWithDateIndex = voucherItems.findIndex(voucher => voucher.extra.valid_on_date !== null);
            const voucherItem = voucherWithDateIndex != -1 ? voucherItems[voucherWithDateIndex] : null;
            if (voucherItem) {
                dispatchFilterIfVoucherHasDate(voucherItem);
            }
        }
    };

    return {
        checkIfVouchersChangeTheDate,
        getVouchersWithDate,
        dispatchFilterIfVoucherHasDate,
    };
};
